import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import AlertDialog from '../AlertDialog';

interface EditGuestModalProps {
  guest: {
    id: string;
    name: string;
    phone: string;
    guests_count: number;
    message: string | null;
  };
  onClose: () => void;
  onSubmit: (data: EditGuestFormData) => Promise<void>;
}

export interface EditGuestFormData {
  name: string;
  phone: string;
  guests_count: number;
  message?: string;
}

const EditGuestModal: React.FC<EditGuestModalProps> = ({ guest, onClose, onSubmit }) => {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<EditGuestFormData>({
    defaultValues: {
      name: guest.name,
      phone: guest.phone,
      guests_count: guest.guests_count,
      message: guest.message || ''
    }
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const onSubmitHandler = async (data: EditGuestFormData) => {
    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Error updating guest:', error);
      setAlertMessage('Erro ao atualizar convidado. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div 
          className="bg-white rounded-lg shadow-xl max-w-md w-full"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
        >
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-xl font-playfair font-medium text-wedding-blue-800">
              Editar Convidado
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmitHandler)} className="p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome completo
                </label>
                <input
                  id="name"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                  {...register('name', { required: 'Nome é obrigatório' })}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <input
                  id="phone"
                  type="tel"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                  {...register('phone', { required: 'Telefone é obrigatório' })}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="guests_count" className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade de acompanhantes
                </label>
                <input
                  id="guests_count"
                  type="number"
                  min="0"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500 ${errors.guests_count ? 'border-red-500' : 'border-gray-300'}`}
                  {...register('guests_count', { 
                    required: 'Este campo é obrigatório',
                    min: { value: 0, message: 'O valor mínimo é 0' },
                    valueAsNumber: true
                  })}
                />
                {errors.guests_count && (
                  <p className="text-red-500 text-xs mt-1">{errors.guests_count.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem (opcional)
                </label>
                <textarea
                  id="message"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
                  {...register('message')}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-wedding-blue-600 text-white rounded-md hover:bg-wedding-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      <AlertDialog
        isOpen={showAlert}
        title="Erro"
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </>
  );
};

export default EditGuestModal;